/**
 * LifecycleTab — unified version & runtime dashboard for a network.
 *
 * Replaces the legacy ``SnapshotsTab`` (kept in-tree for the moment as
 * a reference / fall-back). The single tab now hosts two horizontal
 * sub-strips:
 *
 *   * **Design-time** : Snapshots / Auto-Saves / Releases
 *     (the same rows ``SnapshotsTab`` rendered, but split into three
 *     dedicated buckets so creators stop scrolling past 80 auto-saves
 *     to find their last manual snapshot).
 *
 *   * **Run-time**    : Active / Notified / Paused / Retired / Archived
 *     (the lifecycle state machine introduced in M1+M2 — agentic
 *     network *pools* in their five canonical statuses, with admin-only
 *     RETIRED + ARCHIVED views).
 *
 * Every sub-tab shows a bracketed count, e.g. ``Snapshots (5)``,
 * ``Notified (2)``. Counts come from the existing
 * ``listNetworkSnapshots`` payload (design strip) and the new
 * ``GET /networks/{id}/pools/lifecycle`` endpoint (run strip).
 *
 * All bucket / partition logic lives in
 * ``@stepflow/ui-shared/utils/poolLifecycleBuckets`` so the rules can
 * be unit-tested without booting React.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  Archive,
  History,
  MoreVertical,
  Package,
  Pause,
  PlayCircle,
  RefreshCw,
  Save,
  Send,
  TimerOff,
  Zap,
} from 'lucide-react';
import PortalMenu from '@stepflow/ui-shared/components/PortalMenu.jsx';
import { useToast } from '@stepflow/ui-shell/context/ToastContext.jsx';
import {
  deleteNetworkRelease,
  deleteUserSnapshot,
  downloadSnapshotManifest,
  listNetworkSnapshots,
  reactivateNetworkRelease,
} from '@/services/networkRestoreApi';
import {
  getNetworkPoolsLifecycle,
  pausePool,
  unpausePool,
  restorePool,
} from '@/services/poolLifecycleApi';
import {
  RELEASE_STATUS_DEACTIVATED,
  SNAPSHOT_KIND_BASELINE,
  SNAPSHOT_KIND_RELEASE,
  SNAPSHOT_KIND_USER,
  releaseLifecycleBadge,
  snapshotDomKey,
  snapshotGraphLabel,
  snapshotKindBadge,
  snapshotPrimaryLabel,
  snapshotRowActions,
  snapshotSubLabel,
  sortSnapshotsNewestFirst,
  toRestoreSource,
} from '@/utils/snapshotList';
import {
  DESIGN_BUCKETS,
  DESIGN_BUCKET_AUTO_SAVES,
  DESIGN_BUCKET_RELEASES,
  DESIGN_BUCKET_SNAPSHOTS,
  RUNTIME_BUCKETS,
  RUNTIME_BUCKET_ACTIVE,
  RUNTIME_BUCKET_ARCHIVED,
  RUNTIME_BUCKET_NOTIFIED,
  RUNTIME_BUCKET_PAUSED,
  RUNTIME_BUCKET_RETIRED,
  buildLifecycleTabStrip,
  designBucketCounts,
  inactivityBannerText,
  normalizeRuntimeCounts,
  partitionPoolsForLifecycle,
  partitionSnapshotsForLifecycle,
} from '@/utils/poolLifecycleBuckets';
import { canAccessWorkspaceAdmin } from '@/utils/stepflowRoles';
import { formatRelativeTime } from '@/utils/formatDate';
import { displayVersionLabel } from '@/utils/stepflowVersionLabel';
import { WORKBENCH_OUTLINE_BTN_MODAL } from '@/utils/workbenchOutlineButtonClassNames.js';
import { baselineDriftWarning } from '@/utils/restoreDialog';
import RestoreConfirmDialog from './RestoreConfirmDialog';
import DeactivateReleaseDialog from './DeactivateReleaseDialog';
import PublishToRegistryDialog from './PublishToRegistryDialog';
import RenameSnapshotDialog from './RenameSnapshotDialog';
import RetirePoolDialog from './RetirePoolDialog';
import LifecycleAuditDrawer from './LifecycleAuditDrawer';

// ---------------------------------------------------------------------------
// Visual helpers (snapshot rows reuse the SnapshotsTab palette)
// ---------------------------------------------------------------------------

function snapshotKindPillClasses(entry) {
  const kind = entry?.kind;
  if (kind === SNAPSHOT_KIND_BASELINE) {
    return 'bg-amber-100 text-amber-800 border-amber-200';
  }
  if (kind === SNAPSHOT_KIND_RELEASE) {
    if (entry?.releaseStatus === RELEASE_STATUS_DEACTIVATED) {
      return 'bg-orange-100 text-orange-800 border-orange-200';
    }
    return 'bg-emerald-100 text-emerald-800 border-emerald-200';
  }
  if (kind === SNAPSHOT_KIND_USER) {
    return 'bg-blue-100 text-blue-800 border-blue-200';
  }
  return 'bg-gray-100 text-gray-700 border-gray-200';
}

function lifecycleBadgeClasses(tone) {
  if (tone === 'success') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (tone === 'warning') return 'bg-orange-50 text-orange-700 border-orange-200';
  if (tone === 'danger') return 'bg-red-50 text-red-700 border-red-200';
  return 'bg-gray-50 text-gray-700 border-gray-200';
}

function GraphLabelChip({ entry }) {
  const label = snapshotGraphLabel(entry);
  if (!label) return null;
  const isConfig = label === 'config-only';
  const classes = isConfig
    ? 'bg-sky-50 text-sky-800 border-sky-200'
    : 'bg-violet-50 text-violet-800 border-violet-200';
  const text = isConfig ? 'config only' : 'graph + config';
  const title = isConfig
    ? "Snapshot's Step Functions graph matches the golden baseline — only agent config differs."
    : "Snapshot's Step Functions graph differs from the golden baseline.";
  return (
    <span
      className={`text-[10px] uppercase tracking-wide font-semibold border rounded-full px-2 py-0.5 ${classes}`}
      title={title}
    >
      {text}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Sub-tab strip
// ---------------------------------------------------------------------------

function SubTabButton({ id, label, count, tone = 'neutral', selected, onSelect }) {
  // Notified bucket gets the danger tint so an inactivity backlog catches
  // the operator's eye even when the count is small. Other tones keep
  // the regular underline-on-select language so a glance still tells
  // you which sub-tab is live.
  const dangerTint =
    tone === 'danger' && count > 0 && !selected
      ? 'text-red-600 hover:text-red-700'
      : '';
  const base =
    'shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1 text-[12px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1';
  const palette = selected
    ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
    : `text-gray-600 hover:text-gray-900 hover:bg-white/60 ${dangerTint}`;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      onClick={() => onSelect(id)}
      className={`${base} ${palette}`}
    >
      <span>{label}</span>
      <span
        className={`text-[10px] tabular-nums ${
          selected ? 'text-gray-500' : 'text-gray-400'
        }`}
      >
        ({count})
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Snapshot row + action menu
// ---------------------------------------------------------------------------

function SnapshotRowMenu({ entry, onAction, hasSuccessfulRun }) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const actions = snapshotRowActions(entry, { hasSuccessfulRun });
  if (!actions.length) return null;
  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="rounded-full p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        aria-label="Actions"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreVertical className="h-4 w-4" aria-hidden />
      </button>
      <PortalMenu
        anchorRef={buttonRef}
        open={open}
        onClose={() => setOpen(false)}
        width={176}
        className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
      >
        <ul className="py-1 text-xs">
          {actions.map((a) => (
            <li key={a.id}>
              <button
                type="button"
                disabled={!!a.disabled}
                title={a.title || undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  if (a.disabled) return;
                  setOpen(false);
                  onAction?.(a.id, entry);
                }}
                className={`block w-full px-3 py-2 text-left ${
                  a.disabled
                    ? 'cursor-not-allowed text-gray-400'
                    : a.tone === 'danger'
                      ? 'text-red-600 hover:bg-red-50'
                      : a.tone === 'warning'
                        ? 'text-orange-600 hover:bg-orange-50'
                        : 'text-gray-800 hover:bg-gray-50'
                }`}
                role="menuitem"
              >
                {a.label}
              </button>
            </li>
          ))}
        </ul>
      </PortalMenu>
    </div>
  );
}

function SnapshotRow({ entry, onAction, releases, hasSuccessfulRun }) {
  const lifecycle = releaseLifecycleBadge(entry);
  return (
    <li
      key={snapshotDomKey(entry)}
      className="flex items-center justify-between gap-3 px-4 py-3"
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`text-[10px] uppercase tracking-wide font-semibold border rounded-full px-2 py-0.5 ${snapshotKindPillClasses(entry)}`}
          >
            {snapshotKindBadge(entry)}
          </span>
          {lifecycle && (
            <span
              className={`text-[10px] uppercase tracking-wide font-semibold border rounded-full px-2 py-0.5 ${lifecycleBadgeClasses(lifecycle.tone)}`}
            >
              {lifecycle.label}
            </span>
          )}
          <GraphLabelChip entry={entry} />
          <span className="truncate text-sm font-medium text-gray-900">
            {snapshotPrimaryLabel(entry, releases)}
          </span>
        </div>
        <p className="mt-0.5 truncate text-xs text-gray-500">
          {snapshotSubLabel(entry)}
          {entry.summary?.agentCount != null && (
            <>
              {' '}
              · {entry.summary.agentCount}{' '}
              {entry.summary.agentCount === 1 ? 'agent' : 'agents'}
            </>
          )}
        </p>
      </div>
      <SnapshotRowMenu entry={entry} onAction={onAction} hasSuccessfulRun={hasSuccessfulRun} />
    </li>
  );
}

// ---------------------------------------------------------------------------
// Pool row
// ---------------------------------------------------------------------------

function poolBadgeClasses(bucket) {
  if (bucket === RUNTIME_BUCKET_ACTIVE) {
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  }
  if (bucket === RUNTIME_BUCKET_NOTIFIED) {
    return 'bg-red-50 text-red-700 border-red-200';
  }
  if (bucket === RUNTIME_BUCKET_PAUSED) {
    return 'bg-orange-50 text-orange-700 border-orange-200';
  }
  return 'bg-gray-100 text-gray-600 border-gray-200';
}

function poolBucketLabel(bucket) {
  if (bucket === RUNTIME_BUCKET_ACTIVE) return 'Active';
  if (bucket === RUNTIME_BUCKET_NOTIFIED) return 'Notified';
  if (bucket === RUNTIME_BUCKET_PAUSED) return 'Paused';
  if (bucket === RUNTIME_BUCKET_RETIRED) return 'Retired';
  if (bucket === RUNTIME_BUCKET_ARCHIVED) return 'Archived';
  return 'Active';
}

function poolPrimaryLabel(pool) {
  return (
    pool?.poolName?.trim() ||
    pool?.poolDisplayName?.trim() ||
    pool?.poolId ||
    '(unnamed pool)'
  );
}

function poolSubLabel(pool, bucket, releases) {
  const parts = [];
  if (pool?.poolId && pool.poolId !== poolPrimaryLabel(pool)) {
    parts.push(pool.poolId);
  }
  if (pool?.releaseVersion != null) {
    const lbl = displayVersionLabel(pool, releases);
    if (lbl) parts.push(`Release ${lbl}`);
  }
  if (pool?.lastActivityAt) {
    parts.push(`active ${formatRelativeTime(pool.lastActivityAt)}`);
  } else if (pool?.createdAt) {
    parts.push(`created ${formatRelativeTime(pool.createdAt)}`);
  }
  if (bucket === RUNTIME_BUCKET_RETIRED && pool?.retiredAt) {
    parts.push(`retired ${formatRelativeTime(pool.retiredAt)}`);
  }
  if (bucket === RUNTIME_BUCKET_ARCHIVED && pool?.archivedAt) {
    parts.push(`archived ${formatRelativeTime(pool.archivedAt)}`);
  }
  return parts.join(' · ');
}

function actionsForPool(bucket, isAdmin) {
  // Authorization is enforced server-side; this list only governs which
  // actions appear in the menu so a USER doesn't see buttons they
  // can't click. Pool CREATOR / SUPERVISOR map to the same menu as
  // workspace admins for pause / retire — we differentiate only on
  // RETIRED + ARCHIVED, which require workspace admin to restore.
  // The audit-history action is read-only and shown in every bucket
  // so any pool member can review the transition timeline.
  const audit = { id: 'audit', label: 'View audit history', tone: 'neutral' };
  if (bucket === RUNTIME_BUCKET_ACTIVE || bucket === RUNTIME_BUCKET_NOTIFIED) {
    return [
      { id: 'pause', label: 'Pause network', tone: 'neutral' },
      { id: 'retire', label: 'Retire network…', tone: 'danger' },
      audit,
    ];
  }
  if (bucket === RUNTIME_BUCKET_PAUSED) {
    return [
      { id: 'unpause', label: 'Resume network', tone: 'neutral' },
      { id: 'retire', label: 'Retire network…', tone: 'danger' },
      audit,
    ];
  }
  if (bucket === RUNTIME_BUCKET_RETIRED || bucket === RUNTIME_BUCKET_ARCHIVED) {
    if (isAdmin) {
      return [
        { id: 'restore', label: 'Restore network', tone: 'neutral' },
        audit,
      ];
    }
    return [audit];
  }
  return [audit];
}

function PoolRowMenu({ bucket, isAdmin, onAction }) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const actions = actionsForPool(bucket, isAdmin);
  if (!actions.length) return null;
  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="rounded-full p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        aria-label="Pool actions"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreVertical className="h-4 w-4" aria-hidden />
      </button>
      <PortalMenu
        anchorRef={buttonRef}
        open={open}
        onClose={() => setOpen(false)}
        width={192}
        className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
      >
        <ul className="py-1 text-xs">
          {actions.map((a) => (
            <li key={a.id}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onAction?.(a.id);
                }}
                className={`block w-full px-3 py-2 text-left hover:bg-gray-50 ${
                  a.tone === 'danger'
                    ? 'text-red-600 hover:bg-red-50'
                    : 'text-gray-800'
                }`}
                role="menuitem"
              >
                {a.label}
              </button>
            </li>
          ))}
        </ul>
      </PortalMenu>
    </div>
  );
}

function PoolRow({ pool, bucket, isAdmin, onAction, releases }) {
  const banner = bucket === RUNTIME_BUCKET_NOTIFIED
    ? inactivityBannerText(pool?.inactivityWarning)
    : null;
  return (
    <li className="flex items-start justify-between gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`text-[10px] uppercase tracking-wide font-semibold border rounded-full px-2 py-0.5 ${poolBadgeClasses(bucket)}`}
          >
            {poolBucketLabel(bucket)}
          </span>
          <span className="truncate text-sm font-medium text-gray-900">
            {poolPrimaryLabel(pool)}
          </span>
        </div>
        <p className="mt-0.5 truncate text-xs text-gray-500">
          {poolSubLabel(pool, bucket, releases) || 'no activity yet'}
        </p>
        {banner && (
          <p className="mt-2 inline-flex items-start gap-1.5 rounded-md border border-red-100 bg-red-50 px-2 py-1 text-[11px] leading-snug text-red-700">
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>{banner}</span>
          </p>
        )}
      </div>
      <PoolRowMenu bucket={bucket} isAdmin={isAdmin} onAction={onAction} />
    </li>
  );
}

// ---------------------------------------------------------------------------
// Empty-state copy per bucket
// ---------------------------------------------------------------------------

const DESIGN_EMPTY_COPY = {
  [DESIGN_BUCKET_SNAPSHOTS]:
    'No manual snapshots yet — click Snapshot in the header menu to capture a labeled save point.',
  [DESIGN_BUCKET_AUTO_SAVES]:
    'No auto-saves yet — the platform writes a checkpoint before each restore and around runs so you can always rewind.',
  [DESIGN_BUCKET_RELEASES]:
    'No releases yet — once you cut a release the immutable version + golden baseline appear here.',
};

const RUNTIME_EMPTY_COPY = {
  [RUNTIME_BUCKET_ACTIVE]:
    'No active networks. Release this build to spawn a new pool.',
  [RUNTIME_BUCKET_NOTIFIED]:
    'No networks are flagged for inactivity right now.',
  [RUNTIME_BUCKET_PAUSED]:
    'No paused networks. Pause one from the Active list when you need to freeze runtime activity.',
  [RUNTIME_BUCKET_RETIRED]:
    'No retired networks. Retired networks disappear from operator view; only workspace admins can restore them from here.',
  [RUNTIME_BUCKET_ARCHIVED]:
    'No archived networks. Pools auto-archive after they sit in Retired for the tenant retention window.',
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function LifecycleTab({
  network,
  workspaceRole,
  refreshSignal = 0,
  onLatestUserSnapshotChange,
  onNetworkUpdate,
  onRestored,
}) {
  const networkId = network?.networkId;
  const networkName = network?.name || networkId;
  const isAdmin = canAccessWorkspaceAdmin(workspaceRole);
  const { showToast } = useToast();
  const notify = useCallback(
    (text, type = 'success') => {
      if (text) showToast(text, type);
    },
    [showToast],
  );

  // ---------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------
  const [snapshots, setSnapshots] = useState([]);
  const [baselineSource, setBaselineSource] = useState(null);
  const [checkpointCapacity, setCheckpointCapacity] = useState(10);
  const [userSnapshotCapacity, setUserSnapshotCapacity] = useState(20);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [snapshotsError, setSnapshotsError] = useState(null);

  const [pools, setPools] = useState([]);
  const [serverCounts, setServerCounts] = useState(null);
  const [poolsLoading, setPoolsLoading] = useState(false);
  const [poolsError, setPoolsError] = useState(null);

  const [refreshTick, setRefreshTick] = useState(0);
  const [activeSubTab, setActiveSubTab] = useState(DESIGN_BUCKET_SNAPSHOTS);

  // Action dialog state
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [publishTarget, setPublishTarget] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [retirePoolTarget, setRetirePoolTarget] = useState(null);
  const [auditTargetPool, setAuditTargetPool] = useState(null);

  // ---------------------------------------------------------------------
  // Fetchers
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!networkId || networkId === 'new') return undefined;
    let cancelled = false;
    setSnapshotsLoading(true);
    setSnapshotsError(null);
    listNetworkSnapshots(networkId)
      .then((data) => {
        if (cancelled) return;
        setSnapshots(data.snapshots || []);
        setBaselineSource(data.baselineSource);
        setCheckpointCapacity(data.checkpointCapacity || 10);
        if (typeof data.userSnapshotCapacity === 'number') {
          setUserSnapshotCapacity(data.userSnapshotCapacity);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setSnapshotsError(e?.message || 'Failed to load version history');
      })
      .finally(() => {
        if (!cancelled) setSnapshotsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [networkId, refreshTick, refreshSignal]);

  useEffect(() => {
    if (!networkId || networkId === 'new') return undefined;
    let cancelled = false;
    setPoolsLoading(true);
    setPoolsError(null);
    getNetworkPoolsLifecycle(networkId)
      .then((data) => {
        if (cancelled) return;
        setPools(Array.isArray(data?.pools) ? data.pools : []);
        setServerCounts(data?.counts || null);
      })
      .catch((e) => {
        if (cancelled) return;
        // The lifecycle endpoint is brand-new (M2). If the deployment is
        // mid-rollout the call may 404 — fall back to an empty bucket
        // set rather than blocking the design-time view.
        if (e?.status === 404) {
          setPools([]);
          setServerCounts(null);
        } else {
          setPoolsError(e?.message || 'Failed to load pool lifecycle');
        }
      })
      .finally(() => {
        if (!cancelled) setPoolsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [networkId, refreshTick]);

  const refresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  // ---------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------
  const sortedSnapshots = useMemo(
    () => sortSnapshotsNewestFirst(snapshots),
    [snapshots],
  );
  // Releases extracted from the snapshot list — needed by snapshotPrimaryLabel
  // to render the MAJOR.MINOR ("1.10") label instead of the legacy "v2".
  const releases = useMemo(
    () => sortedSnapshots.filter((s) => s?.kind === SNAPSHOT_KIND_RELEASE),
    [sortedSnapshots],
  );
  const designPartition = useMemo(
    () => partitionSnapshotsForLifecycle(sortedSnapshots),
    [sortedSnapshots],
  );
  const designCounts = useMemo(
    () => designBucketCounts(sortedSnapshots),
    [sortedSnapshots],
  );

  const runtimePartition = useMemo(
    () => partitionPoolsForLifecycle(pools),
    [pools],
  );
  const runtimeCounts = useMemo(() => {
    // Trust server counts when present (they're computed from the same
    // DDB scan that produced ``pools`` and include items the caller
    // is allowed to *count* but not *see*). Fall back to a local
    // partition when the server omits the block.
    if (serverCounts) return normalizeRuntimeCounts(serverCounts);
    return normalizeRuntimeCounts({
      [RUNTIME_BUCKET_ACTIVE]: runtimePartition[RUNTIME_BUCKET_ACTIVE].length,
      [RUNTIME_BUCKET_NOTIFIED]: runtimePartition[RUNTIME_BUCKET_NOTIFIED].length,
      [RUNTIME_BUCKET_PAUSED]: runtimePartition[RUNTIME_BUCKET_PAUSED].length,
      [RUNTIME_BUCKET_RETIRED]: runtimePartition[RUNTIME_BUCKET_RETIRED].length,
      [RUNTIME_BUCKET_ARCHIVED]: runtimePartition[RUNTIME_BUCKET_ARCHIVED].length,
    });
  }, [serverCounts, runtimePartition]);

  const strip = useMemo(
    () => buildLifecycleTabStrip({ design: designCounts, runtime: runtimeCounts }),
    [designCounts, runtimeCounts],
  );

  // Pull the SnapshotsTab "newest user snapshot" beacon up to the
  // workbench page so the header keeps showing "Last snapshot · 12m ago"
  // even after the legacy SnapshotsTab is unmounted.
  const latestCallbackRef = useRef(onLatestUserSnapshotChange);
  useEffect(() => {
    latestCallbackRef.current = onLatestUserSnapshotChange;
  });
  useEffect(() => {
    if (!latestCallbackRef.current) return;
    const latest = sortedSnapshots.find((s) => s?.kind === SNAPSHOT_KIND_USER);
    latestCallbackRef.current(
      latest
        ? { capturedAt: latest.capturedAt, label: latest.label || null }
        : null,
    );
  }, [sortedSnapshots]);

  // ---------------------------------------------------------------------
  // Action handlers — design strip
  // ---------------------------------------------------------------------
  const handleSnapshotAction = useCallback(
    async (actionId, entry) => {
      try {
        if (actionId === 'makeActive') {
          const source = toRestoreSource(entry);
          if (!source) return;
          setRestoreTarget({ source, entry });
          return;
        }
        if (actionId === 'rename' && entry?.kind === SNAPSHOT_KIND_USER) {
          setRenameTarget(entry);
          return;
        }
        const entryLabel = displayVersionLabel(entry) || `release ${entry?.releaseVersion}`;
        if (actionId === 'delete') {
          if (entry?.kind === SNAPSHOT_KIND_RELEASE) {
            if (
              !window.confirm(
                `Delete Release ${entryLabel}? This is permanent.`,
              )
            ) {
              return;
            }
            await deleteNetworkRelease(networkId, Number(entry.releaseVersion));
            notify(`Release ${entryLabel} deleted.`);
          } else {
            if (!window.confirm("Delete this snapshot? This can\u2019t be undone.")) {
              return;
            }
            await deleteUserSnapshot(networkId, String(entry.capturedAt));
            notify('Snapshot deleted.');
          }
          refresh();
          return;
        }
        if (actionId === 'deactivate' && entry?.kind === SNAPSHOT_KIND_RELEASE) {
          setDeactivateTarget(entry);
          return;
        }
        if (actionId === 'publish' && entry?.kind === SNAPSHOT_KIND_RELEASE) {
          setPublishTarget(entry);
          return;
        }
        if (actionId === 'reactivate' && entry?.kind === SNAPSHOT_KIND_RELEASE) {
          await reactivateNetworkRelease(networkId, Number(entry.releaseVersion));
          notify(`Release ${entryLabel} reactivated.`);
          refresh();
          return;
        }
        if (actionId === 'export') {
          const source = toRestoreSource(entry);
          if (!source) {
            notify("This snapshot can\u2019t be exported.", 'error');
            return;
          }
          await downloadSnapshotManifest(networkId, source);
          notify('Manifest downloaded.');
        }
      } catch (e) {
        notify(e?.message || 'Action failed', 'error');
      }
    },
    [networkId, refresh, notify],
  );

  const handleRestoreSuccess = useCallback(
    (response) => {
      setRestoreTarget(null);
      refresh();
      if (response?.pendingReleaseChanges !== undefined) {
        onNetworkUpdate?.({
          pendingReleaseChanges: response.pendingReleaseChanges,
        });
      }
      onRestored?.(response);
      const counts = response?.counts;
      const aslMsg = response?.aslRedeployed
        ? ' Step Functions definition was updated.'
        : '';
      notify(
        counts
          ? `Restored — ${counts.add || 0} added, ${counts.update || 0} updated, ${counts.delete || 0} deleted.${aslMsg}`
          : 'Restored.',
      );
      const warnings = Array.isArray(response?.warnings) ? response.warnings : [];
      warnings.forEach((w) => {
        if (typeof w === 'string' && w.trim()) notify(w, 'warning');
      });
    },
    [refresh, onNetworkUpdate, onRestored, notify],
  );

  const handleDeactivateSuccess = useCallback(
    (response) => {
      const lbl = displayVersionLabel(deactivateTarget)
        || `release ${response?.releaseVersion ?? ''}`.trim();
      setDeactivateTarget(null);
      refresh();
      const n = response?.impactedPools?.length || 0;
      notify(
        n
          ? `Release ${lbl} deactivated. ${n} pool${n === 1 ? '' : 's'} impacted.`
          : `Release ${lbl} deactivated.`,
      );
    },
    [refresh, notify, deactivateTarget],
  );

  const handleRenameSuccess = useCallback(() => {
    setRenameTarget(null);
    refresh();
    notify('Snapshot renamed.');
  }, [refresh, notify]);

  // ---------------------------------------------------------------------
  // Action handlers — run strip
  // ---------------------------------------------------------------------
  const handlePoolAction = useCallback(
    async (actionId, pool) => {
      if (!pool?.poolId) return;
      try {
        if (actionId === 'pause') {
          if (!window.confirm(
            `Pause "${poolPrimaryLabel(pool)}"? Runtime activity will be frozen until you resume.`,
          )) {
            return;
          }
          await pausePool(networkId, pool.poolId);
          notify(`Paused ${poolPrimaryLabel(pool)}.`);
          refresh();
          return;
        }
        if (actionId === 'unpause') {
          await unpausePool(networkId, pool.poolId);
          notify(`Resumed ${poolPrimaryLabel(pool)}.`);
          refresh();
          return;
        }
        if (actionId === 'retire') {
          setRetirePoolTarget(pool);
          return;
        }
        if (actionId === 'audit') {
          setAuditTargetPool(pool);
          return;
        }
        if (actionId === 'restore') {
          if (!window.confirm(
            `Restore "${poolPrimaryLabel(pool)}"? It will become Active again.`,
          )) {
            return;
          }
          await restorePool(networkId, pool.poolId);
          notify(`Restored ${poolPrimaryLabel(pool)}.`);
          refresh();
        }
      } catch (e) {
        notify(e?.message || 'Action failed', 'error');
      }
    },
    [networkId, refresh, notify],
  );

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------
  if (!networkId || networkId === 'new') {
    return (
      <div className="p-6 text-sm text-gray-500">
        Deploy the network first to see its lifecycle.
      </div>
    );
  }

  const driftWarning = baselineDriftWarning(baselineSource);
  const isDesign = DESIGN_BUCKETS.includes(activeSubTab);
  const isRuntime = RUNTIME_BUCKETS.includes(activeSubTab);

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
    >
      <div className="flex items-start justify-between gap-4 border-b border-gray-200 bg-white px-6 py-4">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <History className="h-4 w-4 text-gray-500" aria-hidden />
            Lifecycle
          </h2>
          <p className="mt-1 max-w-2xl text-xs leading-relaxed text-gray-600">
            One place for blueprints and the runtime fleet. Up to{' '}
            <strong className="font-medium text-gray-700">{userSnapshotCapacity}</strong>{' '}
            manual snapshots and{' '}
            <strong className="font-medium text-gray-700">{checkpointCapacity}</strong>{' '}
            auto-saves are kept per network. Released versions live in
            Releases. Active pools that go inactive move to{' '}
            <strong className="font-medium text-gray-700">Notified</strong>,
            then auto-retire and eventually archive — admins can
            pause, retire, or restore from the Run-time strip below.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={refresh}
            disabled={snapshotsLoading || poolsLoading}
            className={WORKBENCH_OUTLINE_BTN_MODAL}
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${
                snapshotsLoading || poolsLoading ? 'animate-spin' : ''
              }`}
              aria-hidden
            />
            Refresh
          </button>
        </div>
      </div>

      {/* Sub-tab strips */}
      <div className="shrink-0 border-b border-gray-100 bg-slate-50/60 px-6 py-2">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
              Design-time
            </span>
            <div className="flex flex-wrap items-center gap-1" role="tablist" aria-label="Design-time buckets">
              {strip.design.map((t) => (
                <SubTabButton
                  key={t.id}
                  id={t.id}
                  label={t.label}
                  count={t.count}
                  selected={activeSubTab === t.id}
                  onSelect={setActiveSubTab}
                />
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
              Run-time
            </span>
            <div className="flex flex-wrap items-center gap-1" role="tablist" aria-label="Run-time buckets">
              {strip.runtime.map((t) => (
                <SubTabButton
                  key={t.id}
                  id={t.id}
                  label={t.label}
                  count={t.count}
                  tone={t.tone}
                  selected={activeSubTab === t.id}
                  onSelect={setActiveSubTab}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Body — only one bucket renders at a time */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {isDesign && (
          <DesignBucketBody
            bucket={activeSubTab}
            entries={designPartition[activeSubTab] || []}
            loading={snapshotsLoading}
            error={snapshotsError}
            driftWarning={
              activeSubTab === DESIGN_BUCKET_RELEASES ? driftWarning : null
            }
            onAction={handleSnapshotAction}
            releases={releases}
            hasSuccessfulRun={network?.hasSuccessfulRun !== false}
          />
        )}

        {isRuntime && (
          <RuntimeBucketBody
            bucket={activeSubTab}
            pools={runtimePartition[activeSubTab] || []}
            loading={poolsLoading}
            error={poolsError}
            isAdmin={isAdmin}
            onAction={handlePoolAction}
            releases={releases}
          />
        )}
      </div>

      {/* Dialogs */}
      {restoreTarget && (
        <RestoreConfirmDialog
          open
          networkId={networkId}
          networkName={networkName}
          source={restoreTarget.source}
          sourceEntry={restoreTarget.entry}
          onClose={() => setRestoreTarget(null)}
          onRestored={handleRestoreSuccess}
        />
      )}
      {deactivateTarget && (
        <DeactivateReleaseDialog
          open
          networkId={networkId}
          networkName={networkName}
          releaseEntry={deactivateTarget}
          onClose={() => setDeactivateTarget(null)}
          onDeactivated={handleDeactivateSuccess}
        />
      )}
      {publishTarget && (
        <PublishToRegistryDialog
          open
          networkId={networkId}
          networkName={networkName}
          releaseEntry={publishTarget}
          onClose={() => setPublishTarget(null)}
          onPublished={(res) => {
            const v = res?.stepflowVersion || (res?.version ? `v${res.version}` : '');
            notify(v ? `Published ${v} to registry (Draft).` : 'Published to registry (Draft).');
          }}
        />
      )}
      {renameTarget && (
        <RenameSnapshotDialog
          open
          networkId={networkId}
          entry={renameTarget}
          onClose={() => setRenameTarget(null)}
          onRenamed={handleRenameSuccess}
        />
      )}
      {retirePoolTarget && (
        <RetirePoolDialog
          open
          networkId={networkId}
          pool={retirePoolTarget}
          onClose={() => setRetirePoolTarget(null)}
          onRetired={(response) => {
            setRetirePoolTarget(null);
            const scheduled = response?.pool?.lifecycleStatus === 'NOTIFIED';
            notify(
              scheduled
                ? `Retirement scheduled for ${poolPrimaryLabel(retirePoolTarget)}.`
                : `Retired ${poolPrimaryLabel(retirePoolTarget)}.`,
            );
            refresh();
          }}
        />
      )}
      <LifecycleAuditDrawer
        networkId={networkId}
        pool={auditTargetPool}
        open={Boolean(auditTargetPool)}
        onClose={() => setAuditTargetPool(null)}
      />
    </motion.section>
  );
}

// ---------------------------------------------------------------------------
// Bucket body components
// ---------------------------------------------------------------------------

function DesignBucketBody({ bucket, entries, loading, error, driftWarning, onAction, releases, hasSuccessfulRun }) {
  // Icon parity with ``RuntimeBucketBody`` — empty buckets get a small
  // glyph next to the explainer so the dashboard never has a row of
  // unstyled grey paragraphs.
  const Icon = (() => {
    if (bucket === DESIGN_BUCKET_SNAPSHOTS) return Save;
    if (bucket === DESIGN_BUCKET_AUTO_SAVES) return Zap;
    if (bucket === DESIGN_BUCKET_RELEASES) return Package;
    return History;
  })();

  return (
    <>
      {driftWarning && (
        <p className="mb-3 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {driftWarning}
        </p>
      )}
      {error && (
        <p className="mb-3 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </p>
      )}
      {loading && entries.length === 0 && (
        <p className="text-xs text-gray-500">Loading version history&hellip;</p>
      )}
      {!loading && entries.length === 0 && !error && (
        <p className="flex items-center gap-2 text-xs text-gray-500">
          <Icon className="h-3.5 w-3.5 text-gray-400" aria-hidden />
          {DESIGN_EMPTY_COPY[bucket]}
        </p>
      )}
      {entries.length > 0 && (
        <ul className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white">
          {entries.map((entry) => (
            <SnapshotRow
              key={snapshotDomKey(entry)}
              entry={entry}
              onAction={onAction}
              releases={releases}
              hasSuccessfulRun={hasSuccessfulRun}
            />
          ))}
        </ul>
      )}
    </>
  );
}

function RuntimeBucketBody({ bucket, pools, loading, error, isAdmin, onAction, releases }) {
  const Icon = (() => {
    if (bucket === RUNTIME_BUCKET_NOTIFIED) return AlertTriangle;
    if (bucket === RUNTIME_BUCKET_PAUSED) return Pause;
    if (bucket === RUNTIME_BUCKET_RETIRED) return TimerOff;
    if (bucket === RUNTIME_BUCKET_ARCHIVED) return Archive;
    if (bucket === RUNTIME_BUCKET_ACTIVE) return PlayCircle;
    return Send;
  })();

  return (
    <>
      {error && (
        <p className="mb-3 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </p>
      )}
      {loading && pools.length === 0 && (
        <p className="text-xs text-gray-500">Loading pool lifecycle&hellip;</p>
      )}
      {!loading && pools.length === 0 && !error && (
        <p className="flex items-center gap-2 text-xs text-gray-500">
          <Icon className="h-3.5 w-3.5 text-gray-400" aria-hidden />
          {RUNTIME_EMPTY_COPY[bucket]}
        </p>
      )}
      {pools.length > 0 && (
        <ul className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white">
          {pools.map((pool) => (
            <PoolRow
              key={pool.poolId}
              pool={pool}
              bucket={bucket}
              isAdmin={isAdmin}
              onAction={(actionId) => onAction(actionId, pool)}
              releases={releases}
            />
          ))}
        </ul>
      )}
    </>
  );
}
